import * as React from 'react';
import { cn } from '@/lib/utils';

export interface SwitchProps {
  checked?: boolean;
  onChange?: (checked: boolean) => void;
  disabled?: boolean;
  children?: React.ReactNode;
  className?: string;
  id?: string;
}

const Switch = React.forwardRef<HTMLButtonElement, SwitchProps>(
  (
    {
      checked = false,
      onChange,
      disabled,
      children,
      className,
      id,
    },
    ref,
  ) => {
    const toggle = (
      <button
        ref={ref}
        id={id}
        type="button"
        role="switch"
        aria-checked={checked}
        aria-label={typeof children === 'string' ? children : undefined}
        disabled={disabled}
        className={cn(
          'relative inline-flex h-5 w-9 shrink-0 cursor-pointer items-center rounded-full p-0.5',
          'transition-[background-color,box-shadow,border-color] duration-200 ease-out',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--inv-accent-ring)] focus-visible:ring-offset-1 focus-visible:ring-offset-[var(--inv-surface)]',
          checked
            ? 'border border-transparent bg-[var(--inv-accent)] shadow-[inset_0_1px_2px_rgba(0,0,0,0.12)]'
            : 'border border-solid border-[var(--inv-border-soft)] bg-[var(--inv-surface-muted)] shadow-[inset_0_1px_2px_rgba(43,33,24,0.05)]',
          !disabled &&
            !checked &&
            'hover:border-[var(--inv-border-strong,#d4c4b0)] hover:bg-[var(--inv-border-soft)]',
          !disabled &&
            checked &&
            'hover:bg-[var(--inv-accent-hover,#00378f)]',
          disabled && 'cursor-not-allowed opacity-50',
        )}
        onClick={() => onChange?.(!checked)}>
        <span
          aria-hidden
          className={cn(
            'pointer-events-none block h-4 w-4 rounded-full bg-white',
            'shadow-[0_1px_2px_rgba(43,33,24,0.14),0_0_0_1px_rgba(43,33,24,0.04)]',
            'transition-transform duration-200 ease-out will-change-transform',
            checked ? 'translate-x-4' : 'translate-x-0',
          )}
        />
      </button>
    );

    if (!children) {
      return toggle;
    }

    return (
      <div
        className={cn(
          'flex items-center justify-between gap-3',
          disabled && 'opacity-50',
          className,
        )}>
        <label
          htmlFor={id}
          className={cn(
            'min-w-0 flex-1 text-[0.8125rem] text-[var(--inv-text)]',
            disabled ? 'cursor-not-allowed' : 'cursor-pointer',
          )}>
          {children}
        </label>
        {toggle}
      </div>
    );
  },
);
Switch.displayName = 'Switch';

export { Switch };
